// The mint flow: price, encoding, sending (ETH mint, token mint with approve),
// waiting for the receipt and the success / failure handling, plus the token
// swap helpers. Mixed into App.vue; moved verbatim from it.
//
// getGasLimitFromEstimate's 10x multiplier is a deliberate Coinbase Wallet
// workaround.
/*global ethers*/
/*global pako*/
import utils from '../utils';
import config from '../config';
import { plotSegments, encodeSegments } from '../pixels';
import {
  log, trackEvent, BASE_RPC_URL, WEBHOOK_URL, LOCAL_STORAGE_KEY_REFERRAL,
  UNISWAP_ROUTER_V3, UNISWAP_QUOTER_V3, WETH_ADDRESS, FEE_TIER,
} from './shared';

export default {
  methods: {
    // Resolves with the mined receipt, or null if none shows up in time.
    // Some wallets don't answer eth_getTransactionReceipt, so a public Base RPC
    // is asked as well.
    waitForReceipt: async function(txHash, timeoutMs = 120000) {
      const askWallet = () => this.provider.request({ method: 'eth_getTransactionReceipt', params: [txHash] });
      const askBase = async () => {
        const res = await fetch(BASE_RPC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'eth_getTransactionReceipt', params: [txHash] }),
        });
        return (await res.json()).result;
      };
      const deadline = Date.now() + timeoutMs;
      while (Date.now() < deadline) {
        for (const ask of [askWallet, askBase]) {
          try {
            const receipt = await ask();
            if (receipt) return receipt;
          } catch (e) {
            log('eth_getTransactionReceipt error', e);
          }
        }
        await utils.wait(2000);
      }
      return null;
    },
    // callback(err, receipt): err only when the transaction was mined and
    // reverted. If no receipt arrives in time the transaction is treated as
    // still processing (callback(null, null)), as before.
    pollForCompletion: async function(txHash, callback) {
      this.trxHashSuccess = txHash;
      const receipt = await this.waitForReceipt(txHash);
      if (receipt && receipt.status !== undefined && parseInt(receipt.status, 16) === 0) {
        callback(new Error('Transaction reverted'), receipt);
        return;
      }
      callback(null, receipt);
    },
    getPixelPrice: async function(x1, y1, x2, y2) {
      log('Getting pixel price');
      const { provider } = await this.getProviderSigner();
      const contract = new ethers.Contract(this.nftAddress, config.NFT_ABI, provider);
      const price = await contract.calculateCurrentPrice(x1, y1, x2, y2);
      log('Pixel price', price, typeof price);
      return price;
    },
    getTokenPrice: async function() {
      log('Getting pixel price');
      const { provider } = await this.getProviderSigner();
      const contract = new ethers.Contract(this.nftAddress, config.NFT_ABI, provider);
      const price = await contract.tokenPrice();
      console.log(price);
      log('Token Pixel price', price, typeof price);
      return price;
    },
    updatePixelPrice: async function() {
      if (this.canMint) {
        if (!this.connectAccountAddress) {
          log('Cant get pixel price until wallet is connected');
          return 0;
        }
        const blockPrice = await this.getPixelPrice(0,0,16,16);
        this.ethPerPixel = ethers.formatEther(blockPrice / BigInt(16*16));
      }
    },
    updateTokenPixelPrice: async function() {
      if (this.canMint) {
        if (!this.connectAccountAddress) {
          log('Cant get token pixel price until wallet is connected');
          this.$refs.routerViewRef.$on('accountConnected', this.updateTokenPixelPrice);
          return 0;
        }
        const blockPrice = await this.getTokenPrice();
        // this.ethPerPixel = ethers.formatEther(blockPrice / BigInt(16*16));
        this.tokenPerPixel = blockPrice;
      }
    },
    doBuy: async function (event) {
      trackEvent('doBuy');
      trackEvent('begin_checkout');
      this.saveEvent('doBuy', {
      });
      if (!this.connectAccountAddress) {
        this.showAlert('Please connect your wallet first.');
        return;
      }
      if (!this.isBaseChain(this.connectAccountChainId)) {
        this.showAlert('Please switch to base network first.');
        return;
      }

      this.buyLoading = true;

      // Check the image location is still available since the page loaded
      if (false) {
        if (this.$refs.routerViewRef) {
          // Call the method from the child component using the ref
          await this.$refs.routerViewRef.fetchData(true);
        }
        
        // Check for latest image and link data before sending to prevent overlap
        const isLocationValid = this.validateImageLocation(window.img_offset_x, window.img_offset_y, window.img_width, window.img_height);
        if (!isLocationValid) {
          this.buyLoading = false;
          this.showAlert('Please review your image placement.');
          return;
        }
      }

      // Refresh pixel price
      try {
        await this.updatePixelPrice();
      } catch(e) {
        console.log(`Error updating pixel price`, e);
      }

      const account = this.connectAccountAddress;
      const amountToSend = this.ethTokenToggle ? this.ethAmt : this.tokenAmt;
      try {
        await this.sendEtherFrom(account, amountToSend, this.ethTokenToggle, this.onBuySent);
      } catch (error) {
        // Anything the senders didn't handle themselves: never leave MINT spinning.
        this.buyLoading = false;
        trackEvent('trxFailed');
        console.error('Error sending:', error);
        this.handleWalletError(error);
      }
    },
    // Called once the mint/send transaction is mined (or still pending after
    // the wait); err means it reverted.
    onBuySent: async function(err, transaction) {
      if (err) {
        this.buyLoading = false;
        trackEvent('trxFailed');
        trackEvent('trxReverted');
        this.saveEvent('trxReverted', { hash: this.trxHashSuccess });
        this.showAlert('The transaction failed on chain and nothing was minted. Please try again.');
        return;
      }
      this.buyLoading = false;
      trackEvent('purchase');
      trackEvent('trxSuccess');
      
      // Show modal
      await this.getRefCode();
      this.$bvModal.show('modal-success');
      // this.showAlert('Your transaction is being processed.');

      try {
        fetch(WEBHOOK_URL+'?trigger=true', {
          method: 'GET',
        })
        .then(response => {
          if (!response.ok) {
            // throw new Error('Network response was not ok ' + response.statusText);
            console.error('Network response was not ok ' + response.statusText);
          }
          return response.json(); // Assuming the response is JSON
        })
        .then(data => {
          console.log('Data received:', data);
        })
        .catch(error => {
          console.error('Fetch error:', error);
        });
        utils.confetti(10000); // PARTY!

        // Send referral data
        log(transaction);
        const refCode = localStorage.getItem(LOCAL_STORAGE_KEY_REFERRAL);
        this.saveEvent('purchase', {
          code: refCode,
          transaction,
        });
        // if (false && refCode) {
        //   this.postToLambda('trxSuccess', {
        //     code: refCode,
        //     from: account,
        //     transaction
        //   });
        // }
      } catch (e) {
        console.error('Fetch error:', e);
      }
    },
    handleWalletError: function(errMsg) {
      const userEnv = this.getEventUserEnv();
      this.postToLambda('walletError', {
        ...userEnv,
        error: errMsg,
      });
      if (errMsg && errMsg.message) {
        errMsg = errMsg.message;
      }
      if (!errMsg) {
        this.showAlert('An error occurred.');
      } else if(errMsg.includes('Insufficient payment')) {
        this.showAlert('Insufficient funds for plot. Please reload the page and try again.');
        trackEvent('walletError:plotClaimed');
      } else if(errMsg.includes('Coordinates overlap')) {
        this.showAlert('This plot has already been claimed.');
        trackEvent('walletError:plotClaimed');
      } else if(errMsg.includes('insufficient funds')) {
        this.showAlert('Insufficient funds for transaction.');
        trackEvent('walletError:insufficientFunds');
      } else if(errMsg.includes('user rejected action')) {
        this.showAlert('User rejected transaction.');
        trackEvent('walletError:reject');
      } else if(errMsg.includes('ERC20: insufficient allowance')) {
        this.showAlert('Please approve the token transfer first.');
        trackEvent('walletError:approve');
      } else if(errMsg.includes('execution reverted') || errMsg.includes('missing revert data')) {
        this.showAlert('Transaction would be reverted.');
        trackEvent('walletError:revert');
      } else {
        this.showAlert(errMsg);
        trackEvent('walletError:unknown');
      }
    },
    sendEtherFrom: async function(account, donation_amount, isEth, callback) {
      trackEvent('trxSending');
      let link_url_el = document.getElementById('link-url');
      var link_url = link_url_el.value;
      
      log('donation amount', donation_amount, typeof donation_amount)
      log('link url', link_url)
      
      if(link_url.indexOf('http') !== 0) {
        this.showAlert('Please provide a URL your image should link to for when someone clicks on it.');
        link_url_el.focus();
        this.buyLoading = false;
        return false;
      }
      
      // Each segment's bid is the running pixel count from getPriceForPosition.
      const pixels_data_strs = plotSegments(
        window.myImageData, window.img_width, window.img_height,
        window.img_offset_x, window.img_offset_y, link_url,
        (row, col) => window.segmentPrices[row][col]
      );

      log('pixels_data_strs', pixels_data_strs)
      var pixels_data_str = pixels_data_strs.join('\n');
      var pixels_data_gzip_str = encodeSegments(pixels_data_strs, pako.deflate);
      
      // log('pixels_data_str: '+pixels_data_str);
      log('pixels_data_str length: '+pixels_data_str.length);
      log('pixels_data_gzip_str length: '+pixels_data_gzip_str.length);
      log('compression ratio: '+(pixels_data_str.length/pixels_data_gzip_str.length));

      if (this.demoMode) {
        callback();
        return;
      }

      if (!isEth) {
        await this.sendPixelsToTokenNFT(pixels_data_gzip_str, donation_amount, account, callback);
        return;
      }
      await this.sendPixelsToNFT(pixels_data_gzip_str, donation_amount, account, callback);
    },
    sendPixelsToNFT: async function(pixels_data_gzip_str, donation_amount, account, callback) {
      log('sendPixelsToNFT', arguments);
      trackEvent('sendPixelsToNFT');

      let trxAmount = ethers.parseEther(donation_amount);
      log('trxAmount', trxAmount, typeof trxAmount);

      const x1 = window.img_offset_x;
      const y1 = window.img_offset_y;
      const x2 = window.img_offset_x + window.img_width;
      const y2 = window.img_offset_y + window.img_height;
      
      try {
        let { provider, signer } = await this.getProviderSigner();

        const nftContract = new ethers.Contract(this.nftAddress, config.NFT_ABI, signer);

        // Someone may have minted this spot since the image was placed.
        if (await this.isPlotTaken(nftContract, x1, y1, x2, y2)) {
          this.buyLoading = false;
          this.showAlert('This plot has already been claimed.');
          trackEvent('walletError:plotClaimed');
          return;
        }

        // Pay what the contract charges now: the amount computed when the image
        // was placed is stale as soon as anyone else mints, and the mint reverts.
        const calculatedPrice = await nftContract.calculateCurrentPrice(x1, y1, x2, y2);
        log('calculatedPrice', calculatedPrice, typeof calculatedPrice, ethers.formatEther(calculatedPrice));
        if (calculatedPrice !== trxAmount) {
          this.saveEvent('priceAdjusted', {
            from: trxAmount.toString(),
            to: calculatedPrice.toString(),
          });
          trxAmount = calculatedPrice;
          this.ethAmt = ethers.formatEther(calculatedPrice);
        }

        if (this.requireEthBalance) {
          // Get wallet balance
          const balance = await provider.getBalance(account);
          log('balance', balance.toString(), trxAmount.toString());
          if (balance < trxAmount) {
            this.buyLoading = false;
            this.showAlert('Insufficient funds for transaction.');
            this.saveEvent('insufficientFunds', {
              trxAmount: trxAmount.toString(),
              balance: balance.toString(),
            });
            return;
          }
        }

        let gasLimit = 10000000; // Fallback if we fail to get an estimate
        let estimatedGas;
        try {
          // Attempt to get a gas etimate for the trx and then buffer it with additional gas
          estimatedGas = await nftContract.mint.estimateGas(
            window.img_offset_x, window.img_offset_y, window.img_offset_x + window.img_width, window.img_offset_y + window.img_height, pixels_data_gzip_str,
            {
              value: trxAmount,
              gasLimit: 10000000, // Set a high gas to try to get the node to estimate beyond 1M
            }
          );
          gasLimit = this.getGasLimitFromEstimate(estimatedGas);
        } catch (e) {
          console.error('Error estimating gas:', e);
          // Save error event
          this.saveEvent('estimateGasErr', {
            msg: e.message || '',
          });
        }

        const overrides = {
          gasLimit: gasLimit,
          value: trxAmount,
        };

        this.saveEvent('trxReq', {
          gasLimit: gasLimit.toString(),
          value: trxAmount.toString(),
        });
        const receipt = await nftContract.mint(window.img_offset_x, window.img_offset_y, window.img_offset_x + window.img_width, window.img_offset_y + window.img_height, pixels_data_gzip_str, overrides);
        trackEvent('trxSent');
        console.log('Transaction receipt:', receipt);
        const txHash = receipt.hash;
        this.pollForCompletion(txHash, callback);
        this.resetBuy();
      } catch (error) {
        this.buyLoading = false;
        trackEvent('trxFailed');
        console.error('Error sending:', error);
        this.handleWalletError(error);
      }
    },
    isPlotTaken: async function(nftContract, x1, y1, x2, y2) {
      try {
        return await nftContract.checkOverlap(x1, y1, x2, y2);
      } catch (e) {
        // Never block a mint because the check itself failed.
        log('checkOverlap failed', e);
        return false;
      }
    },
    checkCanPayWithToken: async function(tokenOnly) {
      if (!this.connectAccountAddress) {
        return;
      }
      try {
        let { provider, signer } = await this.getProviderSigner();

        const token = new ethers.Contract(this.contractAddress, config.TOKEN_ABI, signer);
        const existingBalance = await token.balanceOf(this.connectAccountAddress);

        console.log('Existing balance:', this.contractAddress, existingBalance.toString());

        if (existingBalance > 0) {
          // if this route page is "tang"
          if (this.$route.path === '/tang' || this.$route.path === '/dob' || this.$route.path === '/zora') {
            tokenOnly = true;
          }
          if (!tokenOnly) {
            this.canToggleEthToken = true;
          }
        } else {
          this.canToggleEthToken = false;
        }
      } catch (error) {
        console.error('Error checking token balance:', error);
        this.canToggleEthToken = false;
      }
    },
    getSwapAmountIn: async function(contractAddress, amount) {
      if (!amount) {
        amount = 1;
      }
      log('getSwapAmountIn', amount, typeof amount);
      let { provider, signer } = await this.getProviderSigner();
      const quoter = new ethers.Contract(UNISWAP_QUOTER_V3, [
      {
        "inputs": [
          {
                "components": [
                    {
                        "internalType": "address",
                        "name": "tokenIn",
                        "type": "address"
                    },
                    {
                        "internalType": "address",
                        "name": "tokenOut",
                        "type": "address"
                    },
                    {
                        "internalType": "uint256",
                        "name": "amountIn",
                        "type": "uint256"
                    },
                    {
                        "internalType": "uint24",
                        "name": "fee",
                        "type": "uint24"
                    },
                    {
                        "internalType": "uint160",
                        "name": "sqrtPriceLimitX96",
                        "type": "uint160"
                    }
                ],
                "internalType": "struct IQuoterV2.QuoteExactInputSingleParams",
                "name": "params",
                "type": "tuple"
            }
          ],
          "name": "quoteExactInputSingle",
          "outputs": [
              {
                  "internalType": "uint256",
                  "name": "amountOut",
                  "type": "uint256"
              },
              {
                  "internalType": "uint160",
                  "name": "sqrtPriceX96After",
                  "type": "uint160"
              },
              {
                  "internalType": "uint32",
                  "name": "initializedTicksCrossed",
                  "type": "uint32"
              },
              {
                  "internalType": "uint256",
                  "name": "gasEstimate",
                  "type": "uint256"
              }
          ],
          "stateMutability": "nonpayable",
          "type": "function"
        }
      ], provider);

      // // Get the token decimals
      const tokenContract = new ethers.Contract(contractAddress, [
          "function decimals() external view returns (uint8)",
      ], provider);
      console.log('tokenContract', tokenContract);
      const decimals = await tokenContract.decimals();
      console.log(`Token Decimals: ${decimals}`);

      // Simulate swapping 1 token to get price in WETH
      // const amountIn = ethers.parseUnits(amount.toString(), decimals); // Querying price for 1 token
      
      const params = {
          tokenIn: contractAddress,
          tokenOut: WETH_ADDRESS,
          amountIn: amount,
          fee: FEE_TIER,
          sqrtPriceLimitX96: 0, // No price limit
      };
      log(params);

      try {
        console.log('quoter', quoter);
        const quotedAmountOut = await quoter.quoteExactInputSingle.staticCall(params);
        // console.log(`Quoted Amount Out: ${JSON.stringify(quotedAmountOut)}`);
        const tokenPriceInETH = Number(ethers.formatUnits(quotedAmountOut[0], 18));
        console.log(`Price for ${amount} tokens is ${tokenPriceInETH} ETH`);
        return { tokenPriceInETH: quotedAmountOut[0], decimals };
      } catch(error) {
        console.log(error);
        throw new Error(error);
      }
    },
    previewSwap: async function(amount) {
      // TODO
    },
    doSwap: async function(amount) {
      log('doSwap', amount);
      this.swapLoading = true;
      this.$bvModal.hide('modal-alert');

      const { tokenPriceInETH, decimals } = await this.getSwapAmountIn(this.contractAddress, amount);

      // Account for slippage
      const tokenPriceInETHWithSlippage = tokenPriceInETH * BigInt(105) / BigInt(100);

      let { provider, signer } = await this.getProviderSigner();
      const routerAbi = [
        'function exactInputSingle(tuple(address tokenIn, address tokenOut, uint24 fee, address recipient, uint256 amountIn, uint256 amountOutMinimum, uint160 sqrtPriceLimitX96)) returns (uint256 amountOut)'
      ]
      const router = new ethers.Contract(UNISWAP_ROUTER_V3, routerAbi, signer);
      log('router', router);
      const params = {
        tokenIn: WETH_ADDRESS,
        tokenOut: this.contractAddress,
        fee: FEE_TIER,
        recipient: this.connectAccountAddress,
        amountIn: tokenPriceInETHWithSlippage,
        amountOutMinimum: amount,
        sqrtPriceLimitX96: 0, // No price limit
      };
      log(params);
      log(tokenPriceInETHWithSlippage);

      try {
        const tx = await router.exactInputSingle(params, {
          value: tokenPriceInETHWithSlippage // ethers.formatEther(tokenPriceInETHWithSlippage, 18),
        });
        log('Transaction receipt:', tx);
        const txHash = tx.hash;
        this.pollForCompletion(txHash, (err, transaction) => {
          if (err) {
            console.error('Error in transaction:', err);
            this.swapLoading = false;
            return;
          }
          log('Transaction completed:', transaction);
          this.swapLoading = false;
          this.showAlert('Swap complete!');
          trackEvent('swapSuccess');
        });
      } catch(error) {
        trackEvent('swapFailed');
        console.error('Error sawpping:', error);
        this.handleWalletError(error);
      }
    },
    sendPixelsToTokenNFT: async function(pixels_data_gzip_str, donation_amount, account, callback) {
      log('sendPixelsToTokenNFT', arguments);
      trackEvent('sendPixelsToTokenNFT');

      const trxAmount = ethers.parseUnits(donation_amount, 18);

      // const x1 = window.img_offset_x;
      // const y1 = window.img_offset_y;
      // const x2 = window.img_offset_x + window.img_width;
      // const y2 = window.img_offset_y + window.img_height;
      
      try {
        let { provider, signer } = await this.getProviderSigner();

        const nftContract = new ethers.Contract(this.nftAddress, config.NFT_ABI, signer);
        const tokenContract = new ethers.Contract(this.contractAddress, config.TOKEN_ABI, signer);

        // Check balance is enough for the transaction
        const existingBalance = await tokenContract.balanceOf(account);
        console.log(`Existing balance: ${existingBalance.toString()}, donation amount: ${donation_amount}`);
        if (existingBalance < trxAmount) {
          this.buyLoading = false;
          const amtNeeded = trxAmount - existingBalance;
          this.showAlert(`You don't have enough ${this.symbol} for this transaction`,
            {
              amountNeeded: amtNeeded,
              amountNeededFormatted: parseFloat(ethers.formatEther(amtNeeded)).toFixed(0),
            }
          );
          return;
        }
        
        // Approve NFT contract to spend the token
        const approveTrx = await tokenContract.approve(this.nftAddress, trxAmount);
        console.log(`Approved ${trxAmount} tokens for NFT contract:`, approveTrx);
        const approveReceipt = await approveTrx.wait();
        console.log('Approval receipt:', approveReceipt);
        
        // const estimatedGas = await nftContract.tokenMint.estimateGas(window.img_offset_x, window.img_offset_y, window.img_offset_x + window.img_width, window.img_offset_y + window.img_height, pixels_data_gzip_str);
        // const gasLimit = this.getGasLimitFromEstimate(estimatedGas);
        
        // const overrides = {
        //   gasLimit: gasLimit,
        //   // value: trxAmount,
        // };

        const receipt = await nftContract.tokenMint(window.img_offset_x, window.img_offset_y, window.img_offset_x + window.img_width, window.img_offset_y + window.img_height, pixels_data_gzip_str);
        trackEvent('trxSent');
        console.log('Transaction receipt:', receipt);
        const txHash = receipt.hash;
        this.pollForCompletion(txHash, callback);
        this.resetBuy();
      } catch (error) {
        this.buyLoading = false;
        trackEvent('trxFailed');
        console.error('Error sending:', error);
        this.handleWalletError(error);
      }
    },
    getGasLimitFromEstimate: function(estimatedGas) {
      const gasLimit = estimatedGas * BigInt(1000) / BigInt(100); // 10x the gas price for coinbase mobile wallet
      log(`Estimated gas: ${estimatedGas}, gasLimit: ${gasLimit}`);
      return gasLimit;
    },
  },
};
